/**
 * Helpers for the CRM "New Lead" modal: turn a single-line address into the
 * broken-out address/city/state/zipCode fields our data uses, and derive a
 * website from a business email address.
 */

const STATE_ABBREVIATIONS = {
  alabama: 'AL',
  alaska: 'AK',
  arizona: 'AZ',
  arkansas: 'AR',
  california: 'CA',
  colorado: 'CO',
  connecticut: 'CT',
  delaware: 'DE',
  'district of columbia': 'DC',
  florida: 'FL',
  georgia: 'GA',
  hawaii: 'HI',
  idaho: 'ID',
  illinois: 'IL',
  indiana: 'IN',
  iowa: 'IA',
  kansas: 'KS',
  kentucky: 'KY',
  louisiana: 'LA',
  maine: 'ME',
  maryland: 'MD',
  massachusetts: 'MA',
  michigan: 'MI',
  minnesota: 'MN',
  mississippi: 'MS',
  missouri: 'MO',
  montana: 'MT',
  nebraska: 'NE',
  nevada: 'NV',
  'new hampshire': 'NH',
  'new jersey': 'NJ',
  'new mexico': 'NM',
  'new york': 'NY',
  'north carolina': 'NC',
  'north dakota': 'ND',
  ohio: 'OH',
  oklahoma: 'OK',
  oregon: 'OR',
  pennsylvania: 'PA',
  'rhode island': 'RI',
  'south carolina': 'SC',
  'south dakota': 'SD',
  tennessee: 'TN',
  texas: 'TX',
  utah: 'UT',
  vermont: 'VT',
  virginia: 'VA',
  washington: 'WA',
  'west virginia': 'WV',
  wisconsin: 'WI',
  wyoming: 'WY',
  'puerto rico': 'PR',
  guam: 'GU',
  'virgin islands': 'VI'
};

const STATE_CODES = new Set(Object.values(STATE_ABBREVIATIONS));

/** Street-type words used to find where the street address ends. */
const STREET_SUFFIXES = new Set([
  'st', 'street', 'ave', 'av', 'avenue', 'blvd', 'boulevard', 'rd', 'road',
  'dr', 'drive', 'ln', 'lane', 'ct', 'court', 'cir', 'circle', 'pl', 'place',
  'ter', 'terrace', 'trl', 'trail', 'way', 'pkwy', 'parkway', 'hwy', 'highway',
  'sq', 'square', 'loop', 'run', 'row', 'pike', 'path', 'crossing', 'xing',
  'expy', 'expressway', 'fwy', 'freeway', 'aly', 'alley', 'bnd', 'bend',
  'ext', 'extension', 'plz', 'plaza', 'cv', 'cove', 'pt', 'point', 'ridge',
  'rdg', 'creek', 'crk', 'walk', 'view', 'vw'
]);

/** Words that introduce a unit/suite that still belongs with the street address. */
const UNIT_DESIGNATORS = new Set([
  'suite', 'ste', 'unit', 'apt', 'apartment', '#', 'bldg', 'building',
  'floor', 'fl', 'rm', 'room', 'dept', 'lot', 'trlr', 'space', 'spc'
]);

/** Directionals that can trail a street name after the suffix (e.g. "Main St NW"). */
const DIRECTIONALS = new Set([
  'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw',
  'north', 'south', 'east', 'west', 'northeast', 'northwest', 'southeast', 'southwest'
]);

const cleanToken = (token) => token.replace(/[.,]/g, '').toLowerCase();

/**
 * Parse a free-form single-line address into its parts.
 * Handles the common shapes people paste, with or without commas:
 *   "123 Main St, Suite 200, Durham, NC 27701"
 *   "123 Main St Durham NC 27701"
 *   "Durham, NC 27701"
 * Anything it can't confidently split stays in `address` so nothing is lost.
 *
 * @param {string} input
 * @returns {{ address: string, city: string, state: string, zipCode: string }}
 */
export function parseAddressLine(input) {
  const result = { address: '', city: '', state: '', zipCode: '' };
  if (!input || typeof input !== 'string') return result;

  let rest = input.replace(/\s+/g, ' ').trim();
  if (!rest) return result;

  // Drop a trailing country so it doesn't get mistaken for the city.
  rest = rest
    .replace(/,?\s*(united states of america|united states|u\.?s\.?a\.?|u\.?s\.?)\s*$/i, '')
    .replace(/[,\s]+$/, '')
    .trim();

  // ZIP (5 digit or ZIP+4) at the end.
  const zipMatch = rest.match(/(\d{5}(?:-\d{4})?)\s*$/);
  if (zipMatch) {
    result.zipCode = zipMatch[1];
    rest = rest.slice(0, zipMatch.index).replace(/[,\s]+$/, '').trim();
  }

  // State — full name first, then two-letter code.
  const fullStateEntry = Object.keys(STATE_ABBREVIATIONS).find((name) =>
    new RegExp(`(^|[,\\s])${name}$`, 'i').test(rest)
  );
  if (fullStateEntry) {
    result.state = STATE_ABBREVIATIONS[fullStateEntry];
    rest = rest.slice(0, rest.length - fullStateEntry.length).replace(/[,\s]+$/, '').trim();
  } else {
    const abbrMatch = rest.match(/(?:^|[,\s])([A-Za-z]{2})\.?$/);
    if (abbrMatch && STATE_CODES.has(abbrMatch[1].toUpperCase())) {
      result.state = abbrMatch[1].toUpperCase();
      rest = rest.slice(0, abbrMatch.index).replace(/[,\s]+$/, '').trim();
    }
  }

  if (!rest) return result;

  const commaParts = rest.split(',').map((part) => part.trim()).filter(Boolean);
  if (commaParts.length >= 2) {
    result.city = commaParts[commaParts.length - 1];
    result.address = commaParts.slice(0, -1).join(', ');
    return result;
  }

  // No commas left — find where the street address ends.
  const tokens = commaParts[0].split(' ').filter(Boolean);
  let cut = -1;
  for (let i = tokens.length - 1; i >= 0; i -= 1) {
    if (STREET_SUFFIXES.has(cleanToken(tokens[i]))) {
      cut = i;
      break;
    }
  }

  if (cut >= 0) {
    // Pull trailing directionals and unit numbers into the street address.
    let end = cut;
    while (end + 1 < tokens.length && DIRECTIONALS.has(cleanToken(tokens[end + 1]))) end += 1;
    while (end + 1 < tokens.length && UNIT_DESIGNATORS.has(cleanToken(tokens[end + 1]))) {
      end += 1;
      if (end + 1 < tokens.length) end += 1; // the unit value itself
    }
    if (end + 1 < tokens.length && tokens[end + 1].startsWith('#')) {
      end += tokens[end + 1].length > 1 ? 1 : 2;
      end = Math.min(end, tokens.length - 1);
    }

    result.address = tokens.slice(0, end + 1).join(' ');
    result.city = tokens.slice(end + 1).join(' ');
    return result;
  }

  if (/^\d/.test(tokens[0]) && tokens.length > 2) {
    // Starts with a house number but has no recognizable suffix — assume the
    // last word is the city.
    result.address = tokens.slice(0, -1).join(' ');
    result.city = tokens[tokens.length - 1];
    return result;
  }

  // Nothing street-like: it's a city if we already found a state or ZIP.
  if (result.state || result.zipCode) {
    result.city = tokens.join(' ');
  } else {
    result.address = tokens.join(' ');
  }
  return result;
}

/** Personal mailbox providers — never treated as a company website. */
const PERSONAL_EMAIL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'rocketmail.com',
  'hotmail.com', 'hotmail.co.uk', 'outlook.com', 'live.com', 'msn.com',
  'aol.com', 'icloud.com', 'me.com', 'mac.com', 'protonmail.com', 'proton.me',
  'mail.com', 'gmx.com', 'gmx.net', 'zoho.com', 'yandex.com', 'fastmail.com',
  'comcast.net', 'verizon.net', 'att.net', 'sbcglobal.net', 'bellsouth.net',
  'cox.net', 'charter.net', 'earthlink.net', 'juno.com', 'optonline.net',
  'windstream.net', 'frontier.com', 'roadrunner.com', 'rr.com', 'aim.com'
]);

/**
 * Derive a company website from a business email address.
 * Returns '' for personal mailboxes (gmail, yahoo, ...) and unparseable input.
 *
 * @param {string} email
 * @returns {string} e.g. "https://buildersbreakthrough.com"
 */
export function websiteFromEmail(email) {
  if (!email || typeof email !== 'string') return '';

  const match = email.trim().toLowerCase().match(/^[^@\s]+@([a-z0-9.-]+\.[a-z]{2,})$/);
  if (!match) return '';

  const domain = match[1].replace(/^www\./, '');
  if (PERSONAL_EMAIL_DOMAINS.has(domain)) return '';

  return `https://${domain}`;
}
